import dayjs from 'dayjs';

import {
  type NexoyaContentFilterInput,
  NexoyaContentFilterFieldName,
  NexoyaContentFilterOperator,
} from '../../../types';

import { GLOBAL_DATE_FORMAT } from '../../../utils/dates';

import type { LabelFilterClause } from './types';

/**
 * The label RULE vocabulary for Content Labels v2 — which fields and operators a
 * rule may use, how a clause reads on a chip, the category palette — and how a
 * rule is put on the wire. Deliberately free of React and Apollo so it runs in
 * the `unit` (node) vitest project.
 *
 * Nothing here decides which contents a rule reaches. The backend owns the
 * matcher: stages, counts, clashes and the label sheet's preview all come from
 * the server (`data/labelsRepository`), so a second, client-side reading of a
 * rule could only ever disagree with the one that labels the contents.
 */

/**
 * The category palette and the chip tint maths now live in
 * `src/utils/labelChipColors.ts`, because five surfaces outside Settings draw a
 * label chip and importing `chipColors` from here dragged this module's whole
 * rule vocabulary — and its `NexoyaContentFilter*` imports — into each of their
 * bundles.
 *
 * They are re-exported rather than relocated-and-repointed because the palette
 * genuinely belongs to the label vocabulary this module is: eight call sites
 * name it through `labelsAdapter`, and nothing is gained by rewriting them.
 */
export { LABEL_CATEGORY_COLORS, chipColors } from '../../../utils/labelChipColors';

/**
 * The label rule vocabulary, in the order the picker offers it.
 *
 * The one entry the picker does NOT offer is `DisplayProviderId` — see `UNOFFERED_FIELDS` in
 * `filterOptionAdapter`. It stays in the vocabulary because saved rules still hold it.
 */
export type LabelFilterFieldOption = {
  fieldName: NexoyaContentFilterFieldName;
  label: string;
  /** Which glyph the chip and the picker row use. */
  glyph: 'text' | 'channels' | 'options' | 'target' | 'date';
  /** Channels & Accounts is the only row with a nested submenu. */
  hasSubmenu: boolean;
  supported: boolean;
  /** Why it is disabled, shown on the row. */
  unsupportedReason?: string;
};

/** A closed vocabulary's values, in the shape the shared builder's pick list wants. */
export type LabelFilterValueOption = { value: string; label: string };

/**
 * The matcher resolves a content type by enum KEY and throws on one it does not know, so these
 * can only ever be picked from a list. The same seven `availableFieldOperations` offers a
 * content rule (`ContentType`), named as the rest of the app names them.
 */
export const LABEL_CONTENT_TYPE_VALUES: LabelFilterValueOption[] = [
  { value: 'campaign', label: 'Campaign' },
  { value: 'adset', label: 'Ad Set' },
  { value: 'ad', label: 'Ad' },
  { value: 'campaignGroup', label: 'Campaign Group' },
  { value: 'campaignStrategy', label: 'Campaign Strategy' },
  { value: 'sharedBudget', label: 'Shared Budget' },
  { value: 'sharedBiddingStrategy', label: 'Shared Bidding Strategy' },
];

/** The server's `ContentBudgetType`. Typed free text would match nothing, so it is a list too. */
export const LABEL_BUDGET_TYPE_VALUES: LabelFilterValueOption[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'lifetime', label: 'Lifetime' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'segmented', label: 'Segmented' },
  { value: 'shared', label: 'Shared' },
  { value: 'unknown', label: 'Unknown' },
];

export const LABEL_FILTER_FIELD_OPTIONS: LabelFilterFieldOption[] = [
  {
    fieldName: NexoyaContentFilterFieldName.ParentContentId,
    label: 'Channels & Accounts',
    glyph: 'channels',
    hasSubmenu: true,
    supported: true,
  },
  {
    fieldName: NexoyaContentFilterFieldName.ContentType,
    label: 'Content Type',
    glyph: 'options',
    hasSubmenu: false,
    supported: true,
  },
  {
    fieldName: NexoyaContentFilterFieldName.Title,
    label: 'Title',
    glyph: 'text',
    hasSubmenu: false,
    supported: true,
  },
  {
    fieldName: NexoyaContentFilterFieldName.StartDate,
    label: 'Start Date',
    glyph: 'date',
    hasSubmenu: false,
    supported: true,
  },
  {
    fieldName: NexoyaContentFilterFieldName.EndDate,
    label: 'End Date',
    glyph: 'date',
    hasSubmenu: false,
    supported: true,
  },
  {
    fieldName: NexoyaContentFilterFieldName.BidStrategy,
    label: 'Bid Strategy',
    glyph: 'target',
    hasSubmenu: false,
    supported: true,
  },
  {
    fieldName: NexoyaContentFilterFieldName.LatestMeasurementDataDate,
    label: 'Last Measurement Date',
    glyph: 'date',
    hasSubmenu: false,
    supported: true,
  },
  {
    fieldName: NexoyaContentFilterFieldName.ParentTitle,
    label: 'Parent Title',
    glyph: 'text',
    hasSubmenu: false,
    supported: true,
  },
  {
    fieldName: NexoyaContentFilterFieldName.BudgetType,
    label: 'Budget Type',
    glyph: 'options',
    hasSubmenu: false,
    supported: true,
  },
  {
    fieldName: NexoyaContentFilterFieldName.Objective,
    label: 'Objective',
    glyph: 'options',
    hasSubmenu: false,
    supported: true,
  },
  {
    fieldName: NexoyaContentFilterFieldName.CampaignObjective,
    label: 'Campaign Objective',
    glyph: 'options',
    hasSubmenu: false,
    supported: true,
  },
  /**
   * Not offered as a row of its own: Channels & Accounts above covers a channel, whose
   * "select all" writes out the accounts under it. Kept in the vocabulary so a rule saved
   * while it WAS offered still renders as a named pick list of providers rather than as
   * free text full of ids.
   */
  {
    fieldName: NexoyaContentFilterFieldName.DisplayProviderId,
    label: 'Channels',
    glyph: 'channels',
    hasSubmenu: false,
    supported: true,
  },
];

export const LABEL_FILTER_FIELD_LABELS: Record<string, string> = Object.fromEntries(
  LABEL_FILTER_FIELD_OPTIONS.map((option) => [option.fieldName, option.label]),
);

/**
 * Which glyph marks a clause on this field. Figma 12089-20907 uses a serif T for
 * `Title Contains: …` but a three-slider glyph for `Objective Is: …` — the glyph
 * encodes the field TYPE, so one hardcoded T on every chip throws that away.
 *
 * A field the picker does not offer can still reach here from a saved rule, and
 * text is the safe reading of an unknown field.
 */
export function fieldGlyphFor(fieldName: NexoyaContentFilterFieldName): LabelFilterFieldOption['glyph'] {
  return LABEL_FILTER_FIELD_OPTIONS.find((option) => option.fieldName === fieldName)?.glyph ?? 'text';
}

/**
 * The operators a label rule may use on TEXT and on a pick list. `gte`/`lte` are deliberately
 * absent: on a title they would ask the server to order titles alphabetically, which is not
 * what anyone means by a rule.
 */
export const LABEL_FILTER_OPERATORS = [
  NexoyaContentFilterOperator.Contains,
  NexoyaContentFilterOperator.NotContains,
  NexoyaContentFilterOperator.Eq,
  NexoyaContentFilterOperator.Ne,
] as const;

/**
 * …and the two a DATE may use, which are the two the server offers for `startDate`, `endDate`
 * and `latestMeasurementDataDate` (`availableFieldOperations`). Equality is not among them:
 * the column carries a time, so "is exactly this day" would match almost nothing.
 */
export const LABEL_DATE_OPERATORS = [NexoyaContentFilterOperator.Gte, NexoyaContentFilterOperator.Lte] as const;

/** Sentence-case forms, for a clause read mid-sentence (`describeClause`). The chip uses the Title-case ones below. */
export const LABEL_FILTER_OPERATOR_LABELS: Record<string, string> = {
  [NexoyaContentFilterOperator.Gte]: 'is on or after',
  [NexoyaContentFilterOperator.Lte]: 'is on or before',
  [NexoyaContentFilterOperator.Contains]: 'contains',
  [NexoyaContentFilterOperator.NotContains]: 'does not contain',
  [NexoyaContentFilterOperator.Eq]: 'is',
  [NexoyaContentFilterOperator.Ne]: 'is not',
};

/**
 * Title-case forms, used by the rule CHIP only. Figma 12089-20907 renders
 * `Title Contains: IT OR it OR Italia` — the operator is part of the chip's
 * label, so it is capitalised, and the colon sits after it rather than after
 * the field name.
 */
export const LABEL_FILTER_OPERATOR_CHIP_LABELS: Record<string, string> = {
  [NexoyaContentFilterOperator.Contains]: 'Contains',
  [NexoyaContentFilterOperator.NotContains]: 'Does not contain',
  [NexoyaContentFilterOperator.Eq]: 'Is',
  [NexoyaContentFilterOperator.Ne]: 'Is not',
};

// ── rules ───────────────────────────────────────────────────────────────────

function usableKeywords(clause: LabelFilterClause): string[] {
  return (clause.keywords ?? []).map((keyword) => keyword.trim()).filter(Boolean);
}

/**
 * Whether these filters hold a rule at all: at least one keyword that survives
 * trimming.
 *
 * The label sheet chooses between its "select a filter" empty state and the rule
 * preview from this, and `toContentFilterInput` drops the same blank clauses
 * before anything is sent, so the sheet and the request agree on what "no rule"
 * is. Asking a looser question — `keywords.length > 0`, before trimming — makes
 * a clause of blank keywords count as a rule the repository never previews, and
 * the sheet then shows an empty table with live ticks.
 *
 * The one clause the two still read differently is a Channels & Accounts keyword
 * that is not a provider id, which `toContentFilterInput` also drops. The pick
 * list only ever stores ids, so only a rule written by another client can hold
 * one.
 */
export function hasUsableRule(filters: LabelFilterClause[]): boolean {
  return (filters ?? []).some((clause) => usableKeywords(clause).length > 0);
}

/**
 * The body copy of the "Replace existing labels?" confirmation, worded as Figma
 * 12132-29765 words it. Which selected rows call for it is
 * `rowsCarryingAnotherLabel` in `contentsPanel`.
 */
export function replaceLabelsQuestion(categoryName: string, labelName: string): string {
  return `Some of the selected contents already carry a ${categoryName} label. Assigning ${labelName} will replace their current label.`;
}

// ── presentation helpers ────────────────────────────────────────────────────

/** Is / Is not compare a whole value, so their keywords read as a list rather than as OR-ed alternatives. */
const EXACT_OPERATORS: NexoyaContentFilterOperator[] = [NexoyaContentFilterOperator.Eq, NexoyaContentFilterOperator.Ne];

/**
 * A clause split so the rule chip can style the field, the operator and the
 * keywords differently (prototype `.rulechip b` / `.rulechip .kw`). Five or more
 * keywords stop fitting on one line, so the prototype condenses them to a count
 * instead of listing them.
 */
export type ClauseParts = {
  field: string;
  operator: string;
  keywords: string[];
  collapsed: string | null;
};

/**
 * Exported so the rules DRAWER can hand the same number to the shared filter chip: the
 * drawer chip and the category-card chip describe one and the same rule, and collapsing at
 * two different counts made them disagree about it on screen.
 */
export const KEYWORD_COLLAPSE_THRESHOLD = 5;

export function describeClause(clause: LabelFilterClause): ClauseParts {
  const keywords = usableKeywords(clause);
  // Only an OR list collapses. Figma 12089-20907 renders a five-value Is rule as
  // "Objective Is: Reach, Awareness,…" — a comma list truncated by the chip's own
  // width. "5 selected" would hide the one thing the chip exists to say, and a
  // comma list is compact enough that it does not need hiding.
  const isList = EXACT_OPERATORS.includes(clause.operator);
  return {
    field: LABEL_FILTER_FIELD_LABELS[clause.fieldName] ?? clause.fieldName,
    operator: LABEL_FILTER_OPERATOR_LABELS[clause.operator] ?? clause.operator,
    keywords,
    collapsed: !isList && keywords.length >= KEYWORD_COLLAPSE_THRESHOLD ? `${keywords.length} selected` : null,
  };
}

/** How Figma joins the values of a clause, keyed by operator family. */
export const KEYWORD_OR_SEPARATOR = ' OR ';
export const KEYWORD_LIST_SEPARATOR = ', ';

/**
 * `describeClause` shaped for the rule chip in Figma 12089-20907, which reads
 * `Title Contains: IT OR it OR Italia OR Biglietti`:
 *
 * - the operator is Title Case and carries the colon,
 * - keywords are NOT quoted,
 * - Contains / Does not contain join with a literal ` OR ` (rendered in a
 *   lighter colour than the values, hence `separatorIsWord`),
 * - Is / Is not join with a plain comma list.
 *
 * `text` is the whole chip as one string — the chip button's accessible name,
 * so the name and the rendered parts can never drift apart.
 */
export type ClauseChipParts = ClauseParts & {
  separator: string;
  /** True when the separator is the word OR, which Figma de-emphasises. */
  separatorIsWord: boolean;
  text: string;
};

/**
 * The category card's chip, Figma 12089-20907: `Title Contains: IT OR it` — field,
 * then a Title-case operator carrying the colon.
 */
export function describeClauseChip(clause: LabelFilterClause): ClauseChipParts {
  const { field, keywords, collapsed } = describeClause(clause);
  const operator = LABEL_FILTER_OPERATOR_CHIP_LABELS[clause.operator] ?? clause.operator;
  const isList = EXACT_OPERATORS.includes(clause.operator);
  const separator = isList ? KEYWORD_LIST_SEPARATOR : KEYWORD_OR_SEPARATOR;
  const value = collapsed ?? keywords.join(separator);

  return {
    field,
    operator,
    keywords,
    collapsed,
    separator,
    separatorIsWord: !isList,
    text: `${field} ${operator}: ${value}`.trim(),
  };
}

/**
 * A Channels & Accounts keyword as the provider id it names, or null.
 *
 * Only a plain run of digits counts: `Number()` would also read `'0x10'` as 16 or
 * `'1e3'` as 1000 — a real but DIFFERENT provider — and a provider NAME, from a
 * rule written against names rather than ids, is not an id at all.
 */
function toNumericId(keyword: string): number | null {
  if (!/^\d+$/.test(keyword)) return null;
  const id = Number(keyword);
  return Number.isSafeInteger(id) ? id : null;
}

/**
 * A label's rule clauses as `ContentFilterInput[]` — the value's `filters` on the
 * wire, and the draft the rule preview sends to `filterContents`.
 *
 * The backend passes filter values through untyped, and the two id fields —
 * `displayProviderId` (a channel) and `parentContentId` (an ad account) — compare
 * NUMBERS, so they travel as `numberArr`; every text field keeps `stringArr`.
 * A clause left with nothing usable is dropped rather than sent
 * empty: an empty value is "no rule" by another route, and an empty `filters`
 * list has no defined matcher semantics server-side.
 */
/**
 * The fields the server compares as DATES. A clause on one holds a single day, put on the wire
 * as `value.date` in the same `YYYY-MM-DD` the rest of the app uses — never `stringArr`, which
 * the server would string-match instead of parsing.
 */
export const DATE_FIELDS: NexoyaContentFilterFieldName[] = [
  NexoyaContentFilterFieldName.StartDate,
  NexoyaContentFilterFieldName.EndDate,
  NexoyaContentFilterFieldName.LatestMeasurementDataDate,
];

/**
 * A keyword as the day it names, or null. The picker writes `DD MMM, YYYY` and a saved rule
 * arrives as `YYYY-MM-DD`, so both have to parse; anything that does not is dropped rather
 * than sent as an Invalid Date, which the server would read as the epoch.
 */
function toWireDate(keyword: string): string | null {
  const parsed = dayjs(keyword);
  return parsed.isValid() ? parsed.format(GLOBAL_DATE_FORMAT) : null;
}

/**
 * The two halves of the Channels & Accounts picker. A channel is a PROVIDER id and an account is a
 * CONTENT id, so they name different fields — but both travel as `numberArr`, and the UI keeps
 * either as a string because a keyword is a string.
 */
const NUMERIC_ID_FIELDS: NexoyaContentFilterFieldName[] = [
  NexoyaContentFilterFieldName.DisplayProviderId,
  NexoyaContentFilterFieldName.ParentContentId,
];

export function toContentFilterInput(filters: LabelFilterClause[]): NexoyaContentFilterInput[] {
  return (filters ?? []).flatMap((clause): NexoyaContentFilterInput[] => {
    const keywords = usableKeywords(clause);

    if (DATE_FIELDS.includes(clause.fieldName)) {
      const date = keywords.map(toWireDate).find((day): day is string => day !== null);
      return date ? [{ fieldName: clause.fieldName, operator: clause.operator, value: { date } }] : [];
    }

    if (NUMERIC_ID_FIELDS.includes(clause.fieldName)) {
      const numberArr = keywords.map(toNumericId).filter((id): id is number => id !== null);
      return numberArr.length ? [{ fieldName: clause.fieldName, operator: clause.operator, value: { numberArr } }] : [];
    }

    return keywords.length
      ? [{ fieldName: clause.fieldName, operator: clause.operator, value: { stringArr: keywords } }]
      : [];
  });
}
